import React, { useState, useEffect } from 'react';
import SoundManager from '../utils/SoundManager';

interface PinEntryProps {
  correctPin: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const PinEntry: React.FC<PinEntryProps> = ({ correctPin, onSuccess, onCancel }) => {
  const [pin, setPin] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  
  const handleNumberClick = (num: number) => {
    SoundManager.playSound('keypress');
    setPin(prev => {
      const newPin = prev.length < 6 ? prev + num : prev;
      return newPin;
    });
  };
  
  const handleBackspace = () => {
    SoundManager.playSound('toggle');
    setPin(prev => prev.slice(0, -1));
    setError(null);
  };
  
  const verifyPin = () => {
    if (pin === correctPin) {
      SoundManager.playSound('command');
      onSuccess();
    } else {
      SoundManager.playSound('error');
      setError('INCORRECT PIN');
      // Clear pin after short delay
      setTimeout(() => {
        setPin('');
        setError(null);
      }, 1500);
    }
  };
  
  // Auto-validate when 6 digits are entered
  useEffect(() => {
    if (pin.length === 6) {
      verifyPin();
    }
  }, [pin]);
  
  // Random key positions for security
  const [keypad, setKeypad] = useState<number[]>([]);
  
  useEffect(() => {
    // Shuffle numbers 0-9
    const shuffled = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].sort(() => Math.random() - 0.5);
    setKeypad(shuffled);
  }, []);
  
  return (
    <div className="pin-entry-container flex flex-col items-center">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold mb-2">ENTER SECRET CODE</h3>
        <p className="text-sm text-gray-400">6-DIGIT PIN REQUIRED</p>
      </div>
      
      <div className="pin-display flex space-x-3 mb-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="w-8 h-10 border-2 border-[var(--terminal-border)] flex items-center justify-center text-lg font-bold"
          >
            {pin.length > index ? '*' : ''}
          </div>
        ))}
      </div>
      
      {error && (
        <div className="error-message text-red-500 mb-4 animate-pulse">
          {error}
        </div>
      )}
      
      <div className="keypad grid grid-cols-3 gap-4 mb-6">
        {keypad.map((num) => (
          <button
            key={num}
            onClick={() => handleNumberClick(num)}
            className="w-16 h-16 bg-[var(--terminal-bg)] border-2 border-[var(--terminal-border)] 
                     text-[var(--terminal-text)] text-xl font-bold
                     hover:bg-[#1f3928] active:bg-[#294935] transition-colors"
          >
            {num}
          </button>
        ))}
        <button
          onClick={handleBackspace}
          className="w-16 h-16 bg-[var(--terminal-bg)] border-2 border-[var(--terminal-border)] 
                   text-[var(--terminal-text)] text-xl font-bold
                   hover:bg-[#1f3928] active:bg-[#294935] transition-colors"
        >
          ←
        </button>
        <button
          onClick={verifyPin}
          className="w-16 h-16 bg-[var(--terminal-border)] border-2 border-[var(--terminal-text)] 
                   text-[var(--terminal-text)] text-xl font-bold
                   hover:bg-[#1f3928] active:bg-[#294935] transition-colors"
        >
          ✓
        </button>
      </div>
      
      <button
        onClick={onCancel}
        className="px-4 py-2 bg-[var(--terminal-bg)] border border-[var(--terminal-border)] 
                 text-[var(--terminal-text)] text-sm
                 hover:bg-[#1f3928] transition-colors"
      >
        CANCEL
      </button>
    </div>
  );
};

export default PinEntry;